<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：GPSトラッカー端末判定値設定画面
 *
 * 作成者：2021/11/28 ノア）金
 * 最終修正者：2022/1/24 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.gpsTrackerTerminalConfig.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- データ -->
    <section class="tti-padding-content">
      <div class="tti-content-title gpsTrackerTerminalConfigInfo">
        <span class="contentTitle">{{ $t('master.gpsTrackerTerminalConfig.gpsTrackerTerminalConfigInfo') }}</span>
        <el-select
          class="tti-viewSelect"
          v-model="businessOffice"
          size="small"
          :placeholder="$t('master.viewTitle.placeHolderOfOfficeSelector')"
          @change="onBusinessOfficeChange"
          v-tooltip="{
            content: `${$t('master.viewTitle.placeHolderOfOfficeSelector')}`,
            placement: 'bottom-start',
            delay: { show: 1000, hide: 0 }
          }"
        >
          <el-option
            v-for="item in businessOfficeMasterData"
            :key="item.id"
            :label="getBusinessOfficeLabel(item)"
            :value="item.id"
          ></el-option>
        </el-select>
        <span class="viewTitle">{{ $t('master.gpsTrackerTerminalConfig.version') }}</span>
        <span>{{ gpsTrackerTerminalConfigVersion }}</span>
      </div>
      <div class="tti-content-title title-context">
        {{ $t('master.gpsTrackerTerminalConfig.context') }}
      </div>
      <el-form
        class="tti-form"
        ref="form"
        :disabled="pageLock"
        :model="gpsTrackerTerminalConfigData"
        size="mini"
        :rules="rules"
      >
        <div class="tti-lr-container">
          <!-- クラウドアプリ制御項目 -->
          <div class="left">
            <div class="tti-content-title">
              <span>{{ $t('master.gpsTrackerTerminalConfig.cloudApplicationControlItems') }}</span>
            </div>
            <div class="content">
              <!-- 停止一定時間判定 -->
              <el-form-item class="tti-icon-label-form-item">
                <tti-svg-icon class="tti-label-icon" icon-name="wo_045"></tti-svg-icon>
                <el-form-item
                  :label="$t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgment')"
                  prop="stopFixedTimeJudgmentHours"
                >
                  <el-input
                    class="tti-input"
                    v-model.trim="gpsTrackerTerminalConfigData.stopFixedTimeJudgmentHours"
                    @change="checkStopFixedTimeJudgmentHours"
                    aline="right"
                    size="small"
                  >
                  </el-input>
                  <span class="tti-unit">
                    {{ $t('master.gpsTrackerTerminalConfig.unitHours') }}
                  </span>
                </el-form-item>
                <el-form-item prop="stopFixedTimeJudgmentMinutes">
                  <el-input
                    class="tti-input"
                    v-model.trim="gpsTrackerTerminalConfigData.stopFixedTimeJudgmentMinutes"
                    aline="right"
                    size="small"
                    :disabled="gpsTrackerTerminalConfigData.stopFixedTimeJudgmentHours >= stopFixedTimeJudgmentMaxHours"
                  >
                  </el-input>
                  <span class="tti-unit">
                    {{ $t('master.gpsTrackerTerminalConfig.unitMinutes') }}
                  </span>
                </el-form-item>
              </el-form-item>
            </div>
          </div>
          <!-- GPSﾄﾗｯｶｰ端末設定項目 -->
          <div class="right">
            <div class="tti-content-title">
              <span>{{ $t('master.gpsTrackerTerminalConfig.gpsTrackerDeviceSettingsItems') }}</span>
            </div>
            <!-- 稼働判定 -->
            <div class="content">
              <div class="tti-form-block">{{ $t('master.gpsTrackerTerminalConfig.operationStart') }}</div>
              <div class="tti-form-child tti-form-child-level-1">
                <!-- 稼働開始判定値 -->
                <label>{{ $t('master.gpsTrackerTerminalConfig.operationStartJudgment') }}</label>
                <!-- 加速度センサーインアクティブ検知閾値レンジ  -->
                <el-form-item
                  class="tti-form-child-level-2"
                  :label="$t('master.gpsTrackerTerminalConfig.activeSensorThresholdRange')"
                  prop="activeSensorThresholdRange"
                >
                  <el-select
                    class="tti-selection"
                    v-model="gpsTrackerTerminalConfigData.activeSensorThresholdRange"
                    size="small"
                    @change="onRangeChange"
                  >
                    <el-option
                      v-for="item in thresholdRangeList"
                      :key="item.code"
                      :label="item.name"
                      :value="item.code"
                    ></el-option>
                  </el-select>
                </el-form-item>
                <!-- 加速度センサーインアクティブ検知閾値  -->
                <el-form-item
                  class="tti-form-child-level-2"
                  :label="$t('master.gpsTrackerTerminalConfig.activeSensorThreshold')"
                  prop="activeSensorThreshold"
                >
                  <el-input
                    class="tti-input"
                    v-model.trim="gpsTrackerTerminalConfigData.activeSensorThreshold"
                    aline="right"
                    size="small"
                  >
                  </el-input>
                  <span class="tti-unit">
                    {{ $t('master.gpsTrackerTerminalConfig.unitMG') }}
                  </span>
                </el-form-item>
                <!-- 検知時間（ACT検知時間b LPT） -->
                <el-form-item
                  class="tti-form-child-level-2"
                  :label="$t('master.gpsTrackerTerminalConfig.detectionTimeInactBlpt')"
                  prop="detectionTimeInactBlpt"
                >
                  <el-input
                    class="tti-input"
                    v-model.trim="gpsTrackerTerminalConfigData.detectionTimeInactBlpt"
                    aline="right"
                    size="small"
                  >
                  </el-input>
                  <span class="tti-unit">
                    {{ $t('master.gpsTrackerTerminalConfig.unitSecond') }}
                  </span>
                </el-form-item>
                <!-- 稼働中定期監視 -->
                <label>{{ $t('master.gpsTrackerTerminalConfig.periodicOperationMonitoring') }}</label>
                <!-- 稼働中定期タイマ  -->
                <el-form-item
                  class="tti-form-child-level-2"
                  :label="$t('master.gpsTrackerTerminalConfig.periodicOperationTimer')"
                >
                  <div class="tti-periodicOperationTimer">
                    <el-checkbox v-model="periodicOperationTimerUse" @change="periodicOperationTimerUseChange">
                      {{ $t('master.gpsTrackerTerminalConfig.periodicOperationTimerUse') }}
                    </el-checkbox>

                    <!-- 稼働中定期タイマ：n分後  -->
                    <el-form-item class="tti-form-child-level-3" prop="runningRegularTimerMinutes">
                      <el-select
                        class="tti-selection"
                        v-model="gpsTrackerTerminalConfigData.runningRegularTimerMinutes"
                        size="small"
                        @change="onRangeChange"
                        :disabled="!periodicOperationTimerUse"
                      >
                        <el-option
                          v-for="item in getRunningRegularTimerSetting(
                            settingValues.RUNNING_REGULAR_TIMER_VALUE_SETTING
                          )"
                          :key="item"
                          :label="item"
                          :value="item"
                        ></el-option>
                      </el-select>
                      <span class="tti-unit">
                        {{ $t('master.gpsTrackerTerminalConfig.minutesLater') }}
                      </span>
                    </el-form-item>
                    <!-- 稼働中定期タイマ：n分周期  -->
                    <el-form-item class="tti-form-child-level-3" prop="runningRegularTimer">
                      <el-select
                        class="tti-selection"
                        v-model="gpsTrackerTerminalConfigData.runningRegularTimer"
                        size="small"
                        @change="onRangeChange"
                        :disabled="!periodicOperationTimerUse"
                      >
                        <el-option
                          v-for="item in getRunningRegularTimerSetting(
                            settingValues.RUNNING_REGULAR_TIMER_MINUTES_VALUE_SETTING
                          )"
                          :key="item"
                          :label="item"
                          :value="item"
                        ></el-option>
                      </el-select>
                      <span class="tti-unit">
                        {{ $t('master.gpsTrackerTerminalConfig.intervals') }}
                      </span>
                    </el-form-item>
                    <!-- 稼働中定期タイマ：p回  -->
                    <el-form-item class="tti-form-child-level-3" prop="runningRegularRound">
                      <el-input
                        class="tti-input"
                        v-model.trim="gpsTrackerTerminalConfigData.runningRegularRound"
                        aline="right"
                        size="small"
                      >
                      </el-input>
                      <span class="tti-unit">
                        {{ $t('master.gpsTrackerTerminalConfig.times') }}
                      </span>
                    </el-form-item>
                  </div>
                </el-form-item>
              </div>
              <!-- 停止検知判定値 -->
              <div>
                <div class="tti-form-block">{{ $t('master.gpsTrackerTerminalConfig.stopDetection') }}</div>
                <div class="tti-form-child tti-form-child-level-1">
                  <!-- 加速度センサーレンジ  -->
                  <el-form-item
                    class="tti-form-child-level-2"
                    :label="$t('master.gpsTrackerTerminalConfig.accelerometerActiveDetectionThresholdRange')"
                    prop="accelerometerActiveDetectionThresholdRange"
                  >
                    <el-select
                      class="tti-selection"
                      v-model="gpsTrackerTerminalConfigData.accelerometerActiveDetectionThresholdRange"
                      size="small"
                      @change="onRangeChange"
                    >
                      <el-option
                        v-for="item in thresholdRangeList"
                        :key="item.code"
                        :label="item.name"
                        :value="item.code"
                      ></el-option>
                    </el-select>
                  </el-form-item>
                  <!-- 加速度センサー検知閾値  -->
                  <el-form-item
                    class="tti-form-child-level-2"
                    :label="$t('master.gpsTrackerTerminalConfig.accelerometerActiveDetectionThreshold')"
                    prop="accelerometerActiveDetectionThreshold"
                  >
                    <el-input
                      class="tti-input"
                      v-model.trim="gpsTrackerTerminalConfigData.accelerometerActiveDetectionThreshold"
                      aline="right"
                      size="small"
                    >
                    </el-input>
                    <span class="tti-unit">
                      {{ $t('master.gpsTrackerTerminalConfig.unitMG') }}
                    </span>
                  </el-form-item>
                  <!-- 連続停止時間  -->
                  <el-form-item
                    class="tti-form-child-level-2"
                    :label="$t('master.gpsTrackerTerminalConfig.detectionTimeInactB')"
                    prop="detectionTimeInactB"
                  >
                    <el-input
                      class="tti-input"
                      v-model.trim="gpsTrackerTerminalConfigData.detectionTimeInactB"
                      aline="right"
                      size="small"
                    >
                    </el-input>
                    <span class="tti-unit">
                      {{ $t('master.gpsTrackerTerminalConfig.unitMinutes') }}
                    </span>
                  </el-form-item>
                  <!-- 停止監視タイマー -->
                  <el-form-item
                    class="tti-switch"
                    :label="$t('master.gpsTrackerTerminalConfig.continuousStopMonitoringTimer')"
                  >
                    <el-switch v-model="gpsTrackerTerminalConfigData.continuousStopMonitoringTimer"></el-switch>
                  </el-form-item>
                  <!-- Bluetooth連携  -->
                  <el-form-item class="tti-switch" :label="$t('master.gpsTrackerTerminalConfig.blueTooth')">
                    <el-switch v-model="gpsTrackerTerminalConfigData.coordinatorSettings"></el-switch>
                  </el-form-item>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
      </div>
      <div class="tti-bottom-right-area">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref, Vue } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { cloneDeep } from 'lodash'

import { i18n } from '@/locales'
import MST_BASE from '@/views/master/MST_BASE.vue'
import { getGpsTrackerTerminalConfigData, updateGpsTrackerTerminalConfig } from '@/store/apis'
import { GpsTrackerTerminalConfigData, General, CommonResponse } from '@/models'
import { ThresholdRange, OptPeriodTimerConfig, RunningRegularTimerSettingValues } from '@/commons/constants'
import { generalService, GeneralType } from '@/commons/services/generalService'
import HelperService from '@/service/helperService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { RULES, VALID_PATTERN } from '@/service/validateService'
import { common } from '@/store/modules'
import { localService } from '@/commons/services/localService'

/**
 * GPSトラッカー端末判定値設定画面
 *
 * @class
 */
@Component({
  name: 'mst-gps-trk-set'
})
export default class MST_GPS_TRK_SET extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  /**
   * 画面ID
   */
  pageId = 'MST_GPS_TRK_SET'

  /**
   * GPSトラッカー端末判定値設定値
   */
  gpsTrackerTerminalConfigData: GpsTrackerTerminalConfigData = {} as GpsTrackerTerminalConfigData

  /**
   * 加速度検知閾値レンジ
   */
  thresholdRangeList: General[] = []

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  /**
   * 稼働中定期通信
   */
  periodicOperationTimerUse = true

  /**
   * 稼働中定期タイマ：m分周期 初期値：60
   */
  runningRegularTimerDefaultValue = 60

  /**
   * 稼働中定期タイマ：n分後 初期値：60
   */
  runningRegularTimerMinutesDefaultValue = 60

  /**
   * 停止一定時間判定(時)
   */
  stopFixedTimeJudgmentMaxHours = 72

  /**
   * 稼働中定期 プルダウンのキー
   */
  settingValues = RunningRegularTimerSettingValues

  /**
   * 稼働中定期タイマ：n分後 ドロップダウンボックスの内容
   */
  runningRegularTimerSetting: Record<OptPeriodTimerConfig, Record<RunningRegularTimerSettingValues, Array<number>>> = {
    [OptPeriodTimerConfig.OPT_PERIOD_TIMER_0]: {
      [RunningRegularTimerSettingValues.RUNNING_REGULAR_TIMER_VALUE_SETTING]: [60, 90, 120, 150],
      [RunningRegularTimerSettingValues.RUNNING_REGULAR_TIMER_MINUTES_VALUE_SETTING]: [60, 90, 120, 150]
    },
    [OptPeriodTimerConfig.OPT_PERIOD_TIMER_1]: {
      [RunningRegularTimerSettingValues.RUNNING_REGULAR_TIMER_VALUE_SETTING]: [30, 60, 90, 120, 150],
      [RunningRegularTimerSettingValues.RUNNING_REGULAR_TIMER_MINUTES_VALUE_SETTING]: [30, 60, 90, 120, 150]
    }
  }

  /**
   * 稼働中定期ボックスの内容取得する
   *
   * @param {string} runningRegularTimerSettingValues メッセージ内容
   * @returns {Array<number>} 稼働中定期ボックスの内容
   */
  getRunningRegularTimerSetting(runningRegularTimerSettingValues: RunningRegularTimerSettingValues): Array<number> {
    return this.runningRegularTimerSetting[localService.userInfo.optPeriodTimer as OptPeriodTimerConfig][
      runningRegularTimerSettingValues
    ]
  }

  /**
   * get バージョン
   *
   * @returns {number} バージョン
   */
  get gpsTrackerTerminalConfigVersion(): number {
    return parseInt(this.gpsTrackerTerminalConfigData.version) % 4 || 0
  }

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // 停止一定時間判定(時)
      stopFixedTimeJudgmentHours: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgmentHours')])
            .toString(),
          trigger: 'blur'
        },
        // 数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberRange', [
              i18n.t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgmentHours'),
              0,
              72
            ])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 0~72
            {
              rule: RULES.NUMBERRANGE,
              range: [0, 72],
              message: i18n
                .t('vaildate.common.numberRange', [
                  i18n.t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgmentHours'),
                  0,
                  72
                ])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // 停止一定時間判定(分)
      stopFixedTimeJudgmentMinutes: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgmentMinutes')])
            .toString(),
          trigger: 'blur'
        },
        // 数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberRange', [
              i18n.t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgmentMinutes'),
              0,
              50
            ])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 0~50
            {
              rule: RULES.NUMBERRANGE,
              range: [0, 50],
              message: i18n
                .t('vaildate.common.numberRange', [
                  i18n.t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgmentMinutes'),
                  0,
                  50
                ])
                .toString()
            },
            {
              rule: RULES.MUTIPLE,
              callback: this.stopFixedTimeJudgmentMinutesValid.bind(this)
            }
          ],
          trigger: 'blur'
        }
      ],
      // 加速度センサーアクティブ検知閾値レンジ
      activeSensorThresholdRange: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.activeSensorThresholdRange')])
            .toString(),
          trigger: 'change'
        }
      ],
      // 加速度センサー検知閾値
      activeSensorThreshold: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.activeSensorThreshold')])
            .toString(),
          trigger: 'blur'
        },
        // 数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberRange', [
              i18n.t('master.gpsTrackerTerminalConfig.activeSensorThreshold'),
              0,
              this.getRangMax('activeSensorThresholdRange')
            ])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 4文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 4,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.gpsTrackerTerminalConfig.activeSensorThreshold'), 4])
                .toString()
            },
            // 0~x
            {
              rule: RULES.NUMBERRANGE,
              range: [0, this.getRangMax('activeSensorThresholdRange')],
              message: i18n
                .t('vaildate.common.numberRange', [
                  i18n.t('master.gpsTrackerTerminalConfig.activeSensorThreshold'),
                  0,
                  this.getRangMax('activeSensorThresholdRange')
                ])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // 検知時間（ACT検知時間b LPT）
      detectionTimeInactBlpt: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.detectionTimeInactBlpt')])
            .toString(),
          trigger: 'blur'
        },
        // 数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberRange', [
              i18n.t('master.gpsTrackerTerminalConfig.detectionTimeInactBlpt'),
              1,
              255
            ])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 3文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 3,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.gpsTrackerTerminalConfig.detectionTimeInactBlpt'), 3])
                .toString()
            },
            // 1~255
            {
              rule: RULES.NUMBERRANGE,
              range: [1, 255],
              message: i18n
                .t('vaildate.common.numberRange', [
                  i18n.t('master.gpsTrackerTerminalConfig.detectionTimeInactBlpt'),
                  1,
                  255
                ])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // 稼働中定期タイマ：n分後
      runningRegularTimerMinutes: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.runningRegularTimerMinutes')])
            .toString(),
          trigger: 'blur'
        }
      ],
      // 稼働中定期タイマ：m分周期
      runningRegularTimer: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.runningRegularTimer')])
            .toString(),
          trigger: 'blur'
        }
      ],
      // 稼働中定期タイマ：p回
      runningRegularRound: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.runningRegularRound')])
            .toString(),
          trigger: 'blur'
        },
        // 数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberRange', [i18n.t('master.gpsTrackerTerminalConfig.runningRegularRound'), 0, 62])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 2文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 2,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.gpsTrackerTerminalConfig.runningRegularRound'), 2])
                .toString()
            },
            // 0~62
            {
              rule: RULES.NUMBERRANGE,
              range: [0, 62],
              message: i18n
                .t('vaildate.common.numberRange', [
                  i18n.t('master.gpsTrackerTerminalConfig.runningRegularRound'),
                  0,
                  62
                ])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // 加速度センサーインアクティブ検知閾値レンジ
      accelerometerActiveDetectionThresholdRange: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [
              i18n.t('master.gpsTrackerTerminalConfig.accelerometerActiveDetectionThresholdRange')
            ])
            .toString(),
          trigger: 'change'
        }
      ],
      // 加速度センサーインアクティブ検知閾値
      accelerometerActiveDetectionThreshold: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [
              i18n.t('master.gpsTrackerTerminalConfig.accelerometerActiveDetectionThreshold')
            ])
            .toString(),
          trigger: 'blur'
        },
        // 数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberRange', [
              i18n.t('master.gpsTrackerTerminalConfig.accelerometerActiveDetectionThreshold'),
              150,
              this.getRangMax('accelerometerActiveDetectionThresholdRange')
            ])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 4文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 4,
              message: i18n
                .t('vaildate.common.maxlength', [
                  i18n.t('master.gpsTrackerTerminalConfig.accelerometerActiveDetectionThreshold'),
                  4
                ])
                .toString()
            },
            // 150~x
            {
              rule: RULES.NUMBERRANGE,
              range: [150, this.getRangMax('accelerometerActiveDetectionThresholdRange')],
              message: i18n
                .t('vaildate.common.numberRange', [
                  i18n.t('master.gpsTrackerTerminalConfig.accelerometerActiveDetectionThreshold'),
                  150,
                  this.getRangMax('accelerometerActiveDetectionThresholdRange')
                ])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ],
      // 停止判定時間(INACT検知時間b)
      detectionTimeInactB: [
        // 必須
        {
          required: true,
          message: i18n
            .t('vaildate.common.required', [i18n.t('master.gpsTrackerTerminalConfig.detectionTimeInactB')])
            .toString(),
          trigger: 'blur'
        },
        // 数字
        {
          pattern: VALID_PATTERN[RULES.NUMERIC],
          message: i18n
            .t('vaildate.common.numberRange', [i18n.t('master.gpsTrackerTerminalConfig.detectionTimeInactB'), 1, 87])
            .toString(),
          trigger: 'blur'
        },
        {
          validRule: [
            // 3文字以内
            {
              rule: RULES.MAXLENGTH,
              number: 3,
              message: i18n
                .t('vaildate.common.maxlength', [i18n.t('master.gpsTrackerTerminalConfig.detectionTimeInactB'), 3])
                .toString()
            },
            // 1~87
            {
              rule: RULES.NUMBERRANGE,
              range: [1, 87],
              message: i18n
                .t('vaildate.common.numberRange', [
                  i18n.t('master.gpsTrackerTerminalConfig.detectionTimeInactB'),
                  1,
                  87
                ])
                .toString()
            }
          ],
          trigger: 'blur'
        }
      ]
    }
  }

  /**
   * 停止一定時間判定(max 72時)Check
   *
   * @returns {void}
   */
  checkStopFixedTimeJudgmentHours(): void {
    if (this.gpsTrackerTerminalConfigData.stopFixedTimeJudgmentHours >= this.stopFixedTimeJudgmentMaxHours) {
      this.gpsTrackerTerminalConfigData.stopFixedTimeJudgmentMinutes = 0
    }
    if (Number(this.gpsTrackerTerminalConfigData.stopFixedTimeJudgmentHours) === 0) {
      this.gpsTrackerTerminalConfigData.stopFixedTimeJudgmentMinutes = 10
    }
  }

  /**
   * 停止一定時間判定分 チェック
   *
   * @param {number} value 停止一定時間判定分
   * @returns {any} any
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stopFixedTimeJudgmentMinutesValid(value: number): any {
    if (!value) {
      return false
    }
    // 10倍数
    if (value % 10 !== 0) {
      const message = i18n
        .t('vaildate.common.E00026', [i18n.t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgmentMinutes'), 10])
        .toString()
      return message
    }

    // 最小数チェック : 10 分
    if (Number(this.gpsTrackerTerminalConfigData.stopFixedTimeJudgmentHours) === 0 && Number(value) === 0) {
      const message = i18n
        .t('vaildate.common.numberRange', [
          i18n.t('master.gpsTrackerTerminalConfig.stopFixedTimeJudgment'),
          '10' + i18n.t('master.gpsTrackerTerminalConfig.unitMinutes'),
          '72' + i18n.t('master.gpsTrackerTerminalConfig.unitHours')
        ])
        .toString()
      return message
    }
    return false
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('MST_GPS_TRK_SET > mounted')
    await this.fetchBusinessOfficeMaster()
    this.fetchThresholdRange()
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      await this.fetchGpsTrackerTerminalConfigData()
      return true
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > fetchTableData: e=`, e)
      throw e
    }
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
  }

  /**
   * GPSトラッカー端末判定値設定値を取得する
   *
   * @returns {void}
   */
  async fetchGpsTrackerTerminalConfigData(): Promise<void> {
    try {
      const param = {
        businessOfficeId: this.businessOffice
      }
      this.gpsTrackerTerminalConfigData = await getGpsTrackerTerminalConfigData(param)
      Vue.set(
        this.gpsTrackerTerminalConfigData,
        'stopFixedTimeJudgmentHours',
        Math.floor(this.gpsTrackerTerminalConfigData.stopFixedTimeJudgment / 60).toString()
      )
      Vue.set(
        this.gpsTrackerTerminalConfigData,
        'stopFixedTimeJudgmentMinutes',
        Math.floor(this.gpsTrackerTerminalConfigData.stopFixedTimeJudgment % 60).toString()
      )
      // 稼働中定期通信設定
      if (
        this.gpsTrackerTerminalConfigData.runningRegularTimerMinutes === 0 ||
        this.gpsTrackerTerminalConfigData.runningRegularTimer === 0
      ) {
        this.periodicOperationTimerUse = false
      } else {
        this.periodicOperationTimerUse = true
      }
      this.oldData = cloneDeep(this.gpsTrackerTerminalConfigData)
      this.fetchTableDataAfter()
    } catch (e) {
      this.oldData = cloneDeep(this.gpsTrackerTerminalConfigData)
      this.fetchTableDataAfter()
      this.$log.debug('GpsTrackerTerminalConfig > gpsTrackerTerminalConfigData: e=', e)
      throw e
    }
  }

  /**
   * 稼働中定期通信チェック
   *
   * @returns {void}
   */
  periodicOperationTimerUseChange(): void {
    if (!this.periodicOperationTimerUse) {
      this.gpsTrackerTerminalConfigData.runningRegularTimer = 0
      this.gpsTrackerTerminalConfigData.runningRegularTimerMinutes = 0
      return
    }
    if (this.oldData.runningRegularTimer && this.oldData.runningRegularTimerMinutes) {
      this.gpsTrackerTerminalConfigData.runningRegularTimer = this.oldData.runningRegularTimer
      this.gpsTrackerTerminalConfigData.runningRegularTimerMinutes = this.oldData.runningRegularTimerMinutes
      return
    }
    this.gpsTrackerTerminalConfigData.runningRegularTimer = this.runningRegularTimerDefaultValue
    this.gpsTrackerTerminalConfigData.runningRegularTimerMinutes = this.runningRegularTimerMinutesDefaultValue
  }

  /**
   * 加速度検知閾値レンジを取得する
   *
   * @returns {void}
   */
  async fetchThresholdRange(): Promise<void> {
    try {
      this.thresholdRangeList = await generalService[GeneralType.THRESHOLD_RANGE]
    } catch (e) {
      this.$log.debug('MST_GPS_TRK_SET > fetchThresholdRange: e=', e)
      throw e
    }
  }

  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onCancelButtonClick`)
    if (!this.checkDataChanged()) {
      return
    }

    const confirmCallback = () => {
      this.gpsTrackerTerminalConfigData = cloneDeep(this.oldData)
      this.formRef.clearValidate()
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  async saveTableDataBefore(): Promise<boolean> {
    const valid = await this.formRef.validate()
    return valid
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    this.gpsTrackerTerminalConfigData.stopFixedTimeJudgment =
      Number(this.gpsTrackerTerminalConfigData.stopFixedTimeJudgmentHours * 60) +
      Number(this.gpsTrackerTerminalConfigData.stopFixedTimeJudgmentMinutes)

    const params = {
      ...this.gpsTrackerTerminalConfigData,
      businessOfficeId: this.businessOfficeCache
    }
    await updateGpsTrackerTerminalConfig(params)
  }

  /**
   * エラー時の処理
   *
   * @returns {void}
   * @param {CommonResponse} commonResponse エラーメッセージ
   */
  handlerSaveTableError(commonResponse: CommonResponse): void {
    if (commonResponse.common.code === 'E00093') {
      this.fetchTableData()
      common.setBussinessOfficeStatus(true)
    }
  }

  /**
   * データが変更されましたかをチェックする。
   *
   * @returns {boolean} isChanged
   */
  checkDataChanged(): boolean {
    return HelperService.isObjectDataChanged(this.oldData, this.gpsTrackerTerminalConfigData)
  }

  getRangMax(key: string): number {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const value = this.gpsTrackerTerminalConfigData[key]
    let range = 0
    switch (value) {
      case ThresholdRange.G2:
        range = 2000
        break
      case ThresholdRange.G4:
        range = 4000
        break
      case ThresholdRange.G8:
        range = 8000
        break
      default:
        range = 0
        break
    }

    return range
  }

  onRangeChange(): void {
    this.initRule()
    this.initValidate(this.rules)
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-content-title.gpsTrackerTerminalConfigInfo {
  border-bottom: $C05 solid 1px;
  padding-bottom: 6px;
}

.title-context {
  border-bottom: #d4d4d4 solid 1px;
  display: inline-block;
}

.tti-form {
  ::v-deep .el-input__inner {
    text-align: right;
  }
}

.content {
  overflow: auto;
  height: calc(100% - (226px + 12px * 2 + 36px + (39px + 6px + 10px) - 170px));
}

.tti-selection {
  width: 80px;
}

.tti-selection-active-detection-mode {
  width: 170px;
  margin-left: 85px;
}

.tti-input {
  width: 80px;
}

.tti-form-child {
  margin: 12px 0;
}

::v-deep .el-form-item__content {
  .el-select input {
    text-align: left;
  }

  display: flex;
}

.tti-form-child-level-1,
.tti-form-child-level-2 {
  margin-left: 32px;
}

::v-deep .tti-form-child-level-2 label {
  width: 200px;
  text-align: left;
}

.tti-form-block {
  border-bottom: $C05 solid 1px;
  margin-bottom: 10px;
}

.tti-form-block:nth-last-child(1) {
  border-bottom: none;
  margin-bottom: 0;
}

.tti-unit {
  margin-left: 6px;
}

::v-deep .el-form-item__label {
  padding-right: 24px;
}

.left {
  .content {
    padding: 16px 24px;

    .tti-icon-label-form-item {
      margin-bottom: 6px;

      ::v-deep .el-form-item__label {
        width: 200px;
      }

      .tti-label-icon {
        margin-right: 6px;
      }
    }
  }
}

.right {
  .content {
    padding: 16px;
  }
}

.tti-switch {
  display: flex;
  align-items: center;
}

.tti-form-child-level-2 {
  ::v-deep > .el-form-item__label::before {
    content: '*';
    color: #f56c6c;
    margin-right: 4px;
  }
}
</style>
